import React, { useMemo } from 'react'
import { Dropdown, type DropdownOption } from '../ui/Dropdown.js'
import type { Project } from '../../data/projects.js'
import { searchProjects } from '../../data/projects.js'

interface ProjectSelectorProps {
  onProjectSelect: (project: Project) => void
  selectedProject?: Project | null
  placeholder?: string
  label?: string
  required?: boolean
}

export const ProjectSelector: React.FC<ProjectSelectorProps> = ({ 
  onProjectSelect, 
  selectedProject,
  placeholder = "Search for a project...",
  label = "Project Name/Location",
  required = false
}) => {
  // Convert projects to dropdown options
  const projectOptions = useMemo<DropdownOption[]>(() => {
    const projects = searchProjects('')
    return projects.map(project => ({
      id: project.id,
      label: project.name,
      value: project.id,
      description: `${project.location} • ${project.clientName}`,
      metadata: {
        project: project, // Store the full project in metadata
        status: project.status,
        projectManager: project.projectManager,
        description: project.description
      }
    }))
  }, [])

  // Custom filter function for project search
  const filterProjects = (_options: DropdownOption[], searchTerm: string): DropdownOption[] => {
    const projects = searchProjects(searchTerm)
    return projects.map(project => ({
      id: project.id,
      label: project.name,
      value: project.id,
      description: `${project.location} • ${project.clientName}`,
      metadata: {
        project: project, // Store the full project in metadata
        status: project.status,
        projectManager: project.projectManager,
        description: project.description
      }
    }))
  }

  const getStatusColor = (status: Project['status']) => {
    switch (status) {
      case 'active': return '#10b981'
      case 'completed': return '#3b82f6'
      case 'on-hold': return '#f59e0b'
      case 'cancelled': return '#ef4444'
      default: return '#6b7280'
    }
  }

  const getStatusLabel = (status: Project['status']) => {
    switch (status) {
      case 'active': return 'Active'
      case 'completed': return 'Completed' 
      case 'on-hold': return 'On Hold'
      case 'cancelled': return 'Cancelled'
      default: return status
    }
  }

  // Custom render function for project options
  const renderProjectOption = (option: DropdownOption, isHighlighted: boolean) => {
    const project = option.metadata?.project as Project
    return (
      <div 
        style={{
          padding: '12px 16px',
          cursor: 'pointer',
          backgroundColor: isHighlighted ? '#f3f4f6' : 'transparent',
          borderBottom: '1px solid #e5e7eb',
          transition: 'all 0.15s ease',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
          <div style={{ flex: 1 }}>
            <div style={{ 
              fontWeight: 500, 
              fontSize: '14px', 
              color: '#111827',
              marginBottom: '4px'
            }}>
              {project.name}
            </div>
            <div style={{ 
              fontSize: '12px', 
              color: '#6b7280',
              marginBottom: '4px'
            }}>
              {project.description}
            </div>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '12px', 
              fontSize: '12px', 
              color: '#6b7280'
            }}>
              <span>📍 {project.location}</span>
              <span>👤 {project.projectManager}</span>
            </div>
          </div>
          
          <div style={{ textAlign: 'right', marginLeft: '16px' }}>
            <div style={{ 
              fontSize: '12px', 
              fontWeight: 500, 
              color: getStatusColor(project.status),
              marginBottom: '4px'
            }}>
              {getStatusLabel(project.status)}
            </div>
            <div style={{ 
              fontSize: '12px', 
              color: '#6b7280'
            }}>
              {project.clientName}
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Custom render function for selected project
  const renderSelectedProject = (option: DropdownOption) => {
    const project = option.metadata?.project as Project
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span>{project.name}</span>
        <span style={{ 
          fontSize: '12px', 
          color: '#6b7280',
          backgroundColor: '#f3f4f6',
          padding: '2px 6px',
          borderRadius: '4px'
        }}>
          {getStatusLabel(project.status)}
        </span>
      </div>
    )
  }

  const handleProjectSelect = (option: DropdownOption) => {
    const project = option.metadata?.project as Project
    onProjectSelect(project)
  }

  return (
    <div>
      <Dropdown
        options={projectOptions}
        value={selectedProject?.id}
        onSelect={handleProjectSelect}
        placeholder={placeholder}
        label={label}
        required={required}
        searchable={true}
        renderOption={renderProjectOption}
        renderSelected={renderSelectedProject}
        filterFunction={filterProjects}
      />
      
      {/* Search hint */}
      {!selectedProject && (
        <div style={{ 
          fontSize: '12px', 
          color: '#6b7280', 
          marginTop: '4px'
        }}>
          Start typing to search projects by name, location, or client
        </div>
      )}
    </div>
  )
} 